import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { AppStateFacade } from '@app-state';
import {
    DeploymentAuthenticationTypeControl,
    UiNugetDeploymentSettingsComponent,
} from '@components/ui-forms/deployment/ui-nuget-deployment-settings.component';
import { UiSettingForm } from '@components/ui-forms/ui-setting-form';
import { NugetType } from '@models';
import { DeploymentAuthenticationType } from '@services';

import { takeUntil } from 'rxjs/operators';

export interface IDeploymentSettingsFormValue {
    isSharedFeed?: boolean;
    isInternalDeployment?: boolean;
    externalDeploymentUrl?: string;
    selectedAuthenticationType?: DeploymentAuthenticationType;
    authenticationTypes?: {
        [authenticationType in DeploymentAuthenticationType]: {
            [controlName in DeploymentAuthenticationTypeControl]: string;
        };
    };
}
@Component({
    selector: 'ui-deployment-settings',
    templateUrl: './ui-deployment-settings.component.html',
    styleUrls: ['../ui-setting-form.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiDeploymentSettingsComponent extends UiSettingForm implements AfterViewInit, OnDestroy {
    public NugetType = NugetType;

    @ViewChildren(UiNugetDeploymentSettingsComponent)
    private _nugetDeploymentSettingsList!: QueryList<UiNugetDeploymentSettingsComponent>;

    public get isInitialized() {
        return this._nugetDeploymentSettingsList.toArray().reduce(
            (acc, component) => acc && component.isInitialized, true,
        );
    }

    constructor(
        private _fb: FormBuilder,
        cd: ChangeDetectorRef,
        appState: AppStateFacade,
    ) {
        super(
            cd,
            appState,
        );
    }

    ngAfterViewInit() {
        const componentsList = this._nugetDeploymentSettingsList.toArray();

        this.formGroup = this._fb.array(
            componentsList.map(comp => comp.formGroup),
        );

        componentsList.forEach(
            component => {
                component.formChanges$
                    .subscribe(
                        formUpdate => this._updateSettings(component.nugetType, formUpdate),
                    );
            },
        );

        this.isReadOnly$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(
                isReadOnly => isReadOnly ?
                    this.formGroup.disable() :
                    this.formGroup.enable(),
            );
    }

    private _updateSettings(nugetType: NugetType, formUpdate: IDeploymentSettingsFormValue) {
        const { authenticationTypes, ...payload } = formUpdate;

        if (authenticationTypes) {
            (Object.keys(authenticationTypes) as DeploymentAuthenticationType[])
                .forEach(
                    authenticationType => {
                        Object.assign(payload, authenticationTypes[authenticationType]);
                    },
                );
        }

        Object.assign(this.settings.deployment[nugetType], payload);
    }
}
